import React, { useState, useCallback } from "react";
import { ChevronDown, ChevronRight, Copy, Check, Info } from "lucide-react";

// Data attributes (no digits) so number regex never matches our markup; colors via CSS
const JSON_SYN = { string: ' data-syn="str"', number: ' data-syn="num"', literal: ' data-syn="lit"' };

function highlightJson(code) {
  let html = code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  html = html.replace(/(["'])(?:(?=(\\?))\2.)*?\1/g, '<span' + JSON_SYN.string + '>$&</span>');
  html = html.replace(/\b(\d+)\b/g, '<span' + JSON_SYN.number + '>$&</span>');
  html = html.replace(/\b(true|false|null)\b/g, '<span' + JSON_SYN.literal + '>$&</span>');

  return html;
}

const JsonBlock = ({ json }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [json]);

  return (
    <div className="relative rounded-lg overflow-hidden border border-gray-800 bg-[#0d1117] my-3 json-block-syntax">
      <style>{`
        .json-block-syntax [data-syn="str"] { color: #86efac; }
        .json-block-syntax [data-syn="num"] { color: #fde047; }
        .json-block-syntax [data-syn="lit"] { color: #facc15; }
      `}</style>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors z-10"
        title="Copy JSON"
      >
        {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
      </button>
      <pre className="p-4 overflow-x-auto text-[13px] leading-relaxed text-white">
        <code className="text-white" dangerouslySetInnerHTML={{ __html: highlightJson(json) }} />
      </pre>
    </div>
  );
};

const SubSection = ({ sub }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        {open ? <ChevronDown size={14} className="text-gray-500 flex-shrink-0" /> : <ChevronRight size={14} className="text-gray-500 flex-shrink-0" />}
        <span className="text-sm font-semibold text-gray-800">{sub.title}</span>
      </button>
      {open && (
        <div className="px-4 py-3">
          <p className="text-[13px] text-gray-600 leading-relaxed mb-2">{sub.description}</p>
          {sub.json && <JsonBlock json={sub.json} />}
          {sub.note && (
            <div className="flex items-start gap-2 mt-3 px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-100">
              <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-blue-700 leading-relaxed">{sub.note}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TemplateSendingGuide = ({ guide }) => {
  if (!guide) return null;

  return (
    <div id="template-guide">
      <h1>{guide.title}</h1>
      <p className="apidocs-lead">{guide.subtitle}</p>

      <div className="apidocs-stack">
        {guide.sections.map((section) => (
          <div key={section.id} id={section.id}>
            <h2 className="text-lg font-bold text-gray-900 mb-1">{section.title}</h2>
            <p className="text-[13px] text-gray-600 leading-relaxed mb-4">{section.description}</p>
            <div className="space-y-3">
              {(section.subsections || []).map((sub, idx) => (
                <SubSection key={idx} sub={sub} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 px-4 py-4 rounded-lg bg-gray-50 border border-gray-200">
        <h3 className="text-sm font-bold text-gray-800 mb-2">Quick Reference</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12px] text-gray-600">
          <div className="space-y-1.5">
            <p className="font-semibold text-gray-700">Component Types</p>
            <ul className="space-y-1 ml-3">
              <li><code className="bg-gray-200 px-1 rounded text-[11px]">header</code> — Text variables or media (image/video/document)</li>
              <li><code className="bg-gray-200 px-1 rounded text-[11px]">body</code> — Template body text variables</li>
              <li><code className="bg-gray-200 px-1 rounded text-[11px]">button</code> — Dynamic URL suffix or coupon code</li>
            </ul>
          </div>
          <div className="space-y-1.5">
            <p className="font-semibold text-gray-700">Button Sub-types</p>
            <ul className="space-y-1 ml-3">
              <li><code className="bg-gray-200 px-1 rounded text-[11px]">url</code> — Dynamic URL button with text parameter</li>
              <li><code className="bg-gray-200 px-1 rounded text-[11px]">copy_code</code> — Copy code button with coupon_code parameter</li>
            </ul>
          </div>
          <div className="space-y-1.5">
            <p className="font-semibold text-gray-700">Media Types for Header</p>
            <ul className="space-y-1 ml-3">
              <li><code className="bg-gray-200 px-1 rounded text-[11px]">image</code> — <code className="text-[11px]">{"{ link: \"URL\" }"}</code></li>
              <li><code className="bg-gray-200 px-1 rounded text-[11px]">video</code> — <code className="text-[11px]">{"{ link: \"URL\" }"}</code></li>
              <li><code className="bg-gray-200 px-1 rounded text-[11px]">document</code> — <code className="text-[11px]">{"{ link: \"URL\", filename: \"name.pdf\" }"}</code></li>
            </ul>
          </div>
          <div className="space-y-1.5">
            <p className="font-semibold text-gray-700">Important Notes</p>
            <ul className="space-y-1 ml-3">
              <li>Templates must be approved by Meta before sending</li>
              <li>Parameter order matters — values are mapped by position</li>
              <li>Button index is 0-based and matches template button order</li>
              <li>Media URLs must be publicly accessible</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSendingGuide;
